<template>
  <div
    :class="{
      'cloud-image-fi__wrapper': true,
      'cloud-image-fi__wrapper--full-height': fullHeight,
      'cloud-image-fi__wrapper--inline-edit': inlineEditMode && edit !== null,
    }"
    @click="onClickImage"
  >
    <!-- eslint-disable vue/no-v-html -->
    <div
      v-if="isEditMode && (field?.editable || fieldValue?.editable)"
      v-html="field?.editable || fieldValue?.editable"
    />
    <!-- eslint-enable vue/no-v-html -->
    <res-img
      v-else-if="isMounted"
      :key="finalUrl || ''"
      class="cloud-image-fi"
      :src="finalUrl"
      :alt="finalAlt"
      :title="title"
      :params="cloudImageParams"
      :ratio="aspectRatio"
      :width="finalWidth"
      :height="finalHeight"
      :sizes="sizes"
      :draggable="noDrag ? 'false' : null"
      :data-test="dataTest"
      do-not-replace-u-r-l
    />
    <img
      v-else
      class="cloud-image-fi"
      src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'/%3E"
      :alt="alt || field?.value?.alt"
      :draggable="noDrag ? 'false' : undefined"
      :height="height || field?.value?.height"
      :title="title || undefined"
      :width="width || field?.value?.width"
      :style="
        aspectRatio
          ? {
              aspectRatio: aspectRatio,
            }
          : null
      "
      :data-test="dataTest"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, type Ref, ref } from 'vue';
// @ts-ignore does not offer types
import ResImg from 'vue-cloudimage-responsive-plain';

import { useCloudImage } from '@/composables/cloudImage';
import { useInlineEdit } from '@/composables/inlineEdit';
import type { ImageField } from '@/sitecoreFieldTypes';

defineOptions({
  name: 'CloudImageFi',
});
const { getProxySrc } = useCloudImage();

const props = withDefaults(
  defineProps<{
    field?: {
      value?: {
        src?: string;
        alt?: string;
        width?: string;
        height?: string;
      };
      editable?: string;
    };
    src?: string | null;
    alt?: string | null;
    title?: string | null;
    params?: string | null;
    aspectRatio?: number | null;
    ratio?: number | null;
    width?: number | string | null;
    height?: number | string | null;
    sizes?: object;
    dataTest?: string | null;
    customProxy?: boolean;
    trim?: boolean;
    doNotReplaceUrl?: boolean;
    noDrag?: boolean;
    fullHeight?: boolean;
    fieldName?: string | null | undefined;
  }>(),
  {
    alt: '',
    title: '',
    field: undefined,
    src: undefined,
    params: undefined,
    aspectRatio: undefined,
    ratio: undefined,
    width: undefined,
    height: undefined,
    sizes: undefined,
    dataTest: undefined,
    fieldName: undefined,
  },
);

const {
  inlineEditMode,
  fieldValue: fieldValueMisCast,
  offerEditMode,
  notifyChanges,
  openMediaBrowser,
} = useInlineEdit(props.fieldName);
const fieldValue = fieldValueMisCast as Ref<ImageField>;

const edit = ref<string | null>(null);

const onClickImage = async () => {
  if (!inlineEditMode.value || edit.value === null) return;
  const value = await openMediaBrowser();
  if (value !== null) {
    edit.value = value.ItemMedialUrl.replace(/[&?]thn=1(?:&|$)/, '');
    notifyChanges(true, `<image mediaid="{${value.ItemID}}" />`);
  }
};

const isMounted = ref(false);
onMounted(() => {
  isMounted.value = true;

  if (fieldValue.value && !fieldValue.value.editable && offerEditMode.value) {
    edit.value = fieldValue.value.value?.src || null;
  }
});

const finalUrl = computed<string | null>(() => {
  const src = edit.value || props.src || props.field?.value?.src || fieldValue.value?.value?.src;
  return src ? getProxySrc(src) : null;
});

const finalAlt = computed<string | undefined>(
  () => props.alt || props.field?.value?.alt || fieldValue.value?.value?.alt,
);

const finalWidth = computed<string | undefined>(() => {
  const width = props.width || props.field?.value?.width;
  return width ? String(width) : undefined;
});

const finalHeight = computed<string | undefined>(() => {
  const height = props.height || props.field?.value?.height;
  return height ? String(height) : undefined;
});

const cloudImageParams = computed<string>(() => {
  if (props.trim) {
    if (props.params) {
      return `${props.params}&trim=5`;
    }

    return 'trim=5';
  }

  return props.params || '';
});
</script>

<style scoped src="./cloud-image-fi" />
